import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// AC6 — top-right metric counters per tab.
// Source kinds:
//   "static"  → render staticValue as-is
//   "api"     → GET endpoint, pluck via apiPath ("a.b.c"); error → "—" + console.warn (AC6.7)
//   "derived" → reserved for future client-side compute (OVERDUE counter etc.)
function pluck(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function MetricTile({ metric }) {
  const [value, setValue] = useState(metric.source === "static" ? metric.staticValue : null);
  const [state, setState] = useState(metric.source === "api" ? "loading" : "ready");

  useEffect(() => {
    let cancelled = false;
    if (metric.source !== "api" || !metric.endpoint) return;
    setState("loading");
    api.get(metric.endpoint)
      .then((r) => {
        if (cancelled) return;
        const v = pluck(r.data, metric.apiPath);
        if (v == null) {
          setValue("—");
        } else {
          setValue(v);
        }
        setState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(`[menu-guideline] metric ${metric.key} failed:`, err?.message || err);
        setValue("—");
        setState("error");
      });
    return () => { cancelled = true; };
  }, [metric.endpoint, metric.apiPath, metric.key, metric.source]);

  const showSkeleton = state === "loading";
  return (
    <div className="bg-white border border-stone-200 rounded-sm px-3 py-2 min-w-[120px]"
         data-testid={`metric-${metric.key}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] text-stone-500 font-bold">
        {metric.label}
      </div>
      {showSkeleton ? (
        <div className="h-5 mt-1 bg-stone-100 rounded animate-pulse w-14" />
      ) : (
        <div className="font-serif text-lg font-bold text-stone-900 leading-tight">
          {value}{metric.suffix || ""}
        </div>
      )}
    </div>
  );
}

export default function MetricPanel({ metrics }) {
  if (!metrics || metrics.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="metric-panel">
      {metrics.map((m) => <MetricTile key={m.key} metric={m} />)}
    </div>
  );
}
